import { computed } from 'vue';
import type { UtmData } from './UtmConfig.vue';

/** Convert string/number/boolean from backend to proper boolean */
function toBool(val: any, defaultVal: boolean): boolean {
  if (val === undefined || val === null) return defaultVal;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'string') return val === 'true' || val === '1';
  return Boolean(val);
}

const UTM_FIELDS = [
  'utm_enabled',
  'utm_use_custom',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'utm_source_enabled',
  'utm_medium_enabled',
  'utm_campaign_enabled',
  'utm_content_enabled',
  'utm_term_enabled',
  'utm_id_enabled',
  'utm_custom_params',
] as const;

/**
 * Returns default UTM fields for createDefaultData in operator index.ts files.
 */
export function getDefaultUtmFields(data: Record<string, any>) {
  return {
    utm_enabled: toBool(data.utm_enabled, true),
    utm_use_custom: toBool(data.utm_use_custom, false),
    utm_source: data.utm_source ?? '',
    utm_medium: data.utm_medium ?? '',
    utm_campaign: data.utm_campaign ?? '',
    utm_content: data.utm_content ?? '',
    utm_term: data.utm_term ?? '',
    utm_id: data.utm_id ?? '',
    utm_source_enabled: toBool(data.utm_source_enabled, true),
    utm_medium_enabled: toBool(data.utm_medium_enabled, true),
    utm_campaign_enabled: toBool(data.utm_campaign_enabled, true),
    utm_content_enabled: toBool(data.utm_content_enabled, false),
    utm_term_enabled: toBool(data.utm_term_enabled, false),
    utm_id_enabled: toBool(data.utm_id_enabled, false),
    utm_custom_params: data.utm_custom_params ?? [],
  };
}

/**
 * Returns a computed getter/setter for UTM data, used in Form.vue files.
 * @param dataFields - the reactive operator data fields
 */
export function useUtmData(dataFields: Record<string, any>) {
  return computed<UtmData>({
    get: () => ({
      utm_enabled: toBool(dataFields.utm_enabled, true),
      utm_use_custom: toBool(dataFields.utm_use_custom, false),
      utm_source: dataFields.utm_source ?? '',
      utm_medium: dataFields.utm_medium ?? '',
      utm_campaign: dataFields.utm_campaign ?? '',
      utm_content: dataFields.utm_content ?? '',
      utm_term: dataFields.utm_term ?? '',
      utm_id: dataFields.utm_id ?? '',
      utm_source_enabled: toBool(dataFields.utm_source_enabled, true),
      utm_medium_enabled: toBool(dataFields.utm_medium_enabled, true),
      utm_campaign_enabled: toBool(dataFields.utm_campaign_enabled, true),
      utm_content_enabled: toBool(dataFields.utm_content_enabled, false),
      utm_term_enabled: toBool(dataFields.utm_term_enabled, false),
      utm_id_enabled: toBool(dataFields.utm_id_enabled, false),
      utm_custom_params: dataFields.utm_custom_params ?? [],
    }),
    set: (val: UtmData) => {
      for (const key of UTM_FIELDS) {
        dataFields[key] = val[key];
      }
    },
  });
}
